import { z } from "zod";
import { type PromoCode } from "../../firestore";
import { type FunctionObjectReturn } from "../utils";

// Schema for a single promo code
export const PromoCodeSchema = z.object({
  code: z
    .string()
    .min(6, { message: "Der Code muss mindestens 6 Zeichen lang sein" })
    .max(20, { message: "Der Code darf nicht länger als 20 Zeichen sein" }),
  type: z.literal("freeMonths"),
  codeValue: z
    .number({
      error: (issue) =>
        issue.input === undefined
          ? "Die Anzahl der Monate ist erforderlich"
          : "Die Anzahl der Monate muss eine ganze Zahl sein",
    })
    .int({ message: "Die Anzahl der Monate muss eine ganze Zahl sein" })
    .min(1, { message: "Die Anzahl der Monate muss mindestens 1 sein" })
    .max(12, { message: "Die Anzahl der Monate darf nicht größer als 12 sein" }),
  expires_on: z
    .string({
      error: (issue) =>
        issue.input === undefined ? "Die Ablaufdatum ist erforderlich" : "Bitte gib ein Ablaufdatum ein",
    })
    .refine(
      (dateStr) => {
        try {
          // Validate that it's a proper ISO date string
          const date = new Date(dateStr);
          return !isNaN(date.getTime());
        } catch {
          return false;
        }
      },
      { message: "Ungültiges Datumsformat. Bitte verwende das ISO-Format." },
    ),
});

/* Creation */
export const PromoCodeCreateSchema = z
  .array(PromoCodeSchema)
  .nonempty({ message: "Bitte gib mindestens einen Promo-Code ein" })
  .max(1000, { message: "Du kannst maximal 1000 Promo-Codes erstellen" });

export type PromoCodeCreatePostBody = z.infer<typeof PromoCodeCreateSchema>;
export type PromoCodeCreateResponse = FunctionObjectReturn<PromoCodeList>;

/* List (Paginated) */
export const PromoCodeListRequestSchema = z.object({
  limit: z.number().int().min(1).max(50).optional().default(50),
  cursorDocId: z.string().optional(),
  sort: z.enum(["createdOnIsoDate", "expiresOnIsoDate", "code"]).optional().default("createdOnIsoDate"),
  sortDirection: z.enum(["asc", "desc"]).optional().default("desc"),
});
export type PromoCodeListRequest = z.infer<typeof PromoCodeListRequestSchema>;

export interface PromoCodeWithIsoCodeAndDocumentId extends Pick<PromoCode, "code" | "type" | "codeValue"> {
  createdOnIsoDate: string;
  expiresOnIsoDate: string;
  usedOnIsoDate: string | undefined;
  documentId: string;
}

export interface PromoCodeList {
  promoCodes: PromoCodeWithIsoCodeAndDocumentId[];
  total: number;
  cursorDocId: string | undefined;
  hasNextPage: boolean;
}
export type PromoCodeListResponse = FunctionObjectReturn<PromoCodeList>;

/* Redeem */
export const PromoCodeRedeemSchema = z.object({
  code: z
    .string({ error: (issue) => (issue.input === undefined ? "Bitte gib einen Promo-Code ein" : "") })
    .min(6, { message: "Der Code muss mindestens 6 Zeichen lang sein" }),
});
export type PromoCodeRedeemPostBody = z.infer<typeof PromoCodeRedeemSchema>;

export enum PromoCodeRedeemResolution {
  Success = "Success",
  CodeNotFound = "CodeNotFound",
  CodeExpired = "CodeExpired",
  CodeAlreadyUsed = "CodeAlreadyUsed",
  MaxAttemptsExceeded = "MaxAttemptsExceeded",
  UserHasSubscription = "UserHasSubscription",
}
export interface PromoCodeRedeemMessage {
  message: string;
  resolution?: PromoCodeRedeemResolution;
  type?: PromoCode["type"];
  codeValue?: PromoCode["codeValue"];
}
export type PromoCodeRedeemResponse = FunctionObjectReturn<PromoCodeRedeemMessage>;

/* Delete */
export const PromoCodeDeleteSchema = z.object({
  documentIds: z.array(z.string()).min(1, { message: "Bitte gib mindestens einen Dokumenten-Id ein" }),
});
export type PromoCodeDeletePostBody = z.infer<typeof PromoCodeDeleteSchema>;
